import React, { useEffect, useState } from 'react';
import Toggle from '../../../components/Toggle';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';

/* P13 #34/N: the V1 "Custom Search" multi-value paste box (search_quote.pl:1195-1382).
 * Paste many values (one per line or comma-separated), pick a field, and match
 * any of them. Implemented on top of the custom-filter engine: it builds a
 * one-slot blob `field~~=~~v1,v2,v3~~` which the backend turns into IN(...). */

const SEARCH_FIELDS: { field: string; label: string }[] = [
  { field: 'quotenumber', label: 'Quote #' },
  { field: 'customername', label: 'Customer' },
  { field: 'billtoname', label: 'Bill To' },
  { field: 'sage_tag', label: 'Sage Tag' },
  { field: 'salesforceopportunityid', label: 'Salesforce ID' },
  // Cust Tariff # — the customer FSC tariff on the Direct lane (directquote.fsctariff).
  { field: 'fsctariff', label: 'Tariff #' },
  { field: 'status', label: 'Status' },
  { field: 'businesstype', label: 'Business Type' },
  { field: 'soldby', label: 'Sold By' },
];

/** Split pasted text on newlines/commas → a one-slot equality custom-filter blob
 *  (matches any of the values via the engine's comma → IN). "" when empty. */
export function buildSearchBlob(field: string, raw: string): string {
  const values = (raw || '').split(/[\n,]+/).map((v) => v.trim()).filter(Boolean);
  if (!field || values.length === 0) return '';
  return `${field}~~=~~${values.join(',')}~~|||`;
}

/** Describe a custom-search blob for the active-search banner: the searched field's
 *  label + its comma-joined values. Returns null for anything that isn't a search
 *  blob (so the page can fall back to the filter summary). */
export function describeSearchBlob(blob: string): { label: string; values: string } | null {
  const raw = (blob || '').split('|||').filter(Boolean)[0];
  if (!raw) return null;
  const [field = '', , values = ''] = raw.split('~~');
  if (!field) return null;
  const label = SEARCH_FIELDS.find((f) => f.field === field)?.label ?? field;
  return { label, values: values.split(',').map((v) => v.trim()).filter(Boolean).join(', ') };
}

interface Props {
  open: boolean;
  onClose: () => void;
  onApply: (blob: string, searchEnded: boolean) => void;
  /** Active search blob (field~~=~~v1,v2~~) — prefilled for edit/re-run on reopen. */
  initialBlob?: string;
  /** Persisted "Search Ended" toggle state (from user settings) — the dialog opens
   *  with this and returns the current value via onApply. */
  initialSearchEnded?: boolean;
}

const CustomSearchDialog: React.FC<Props> = ({ open, onClose, onApply, initialBlob, initialSearchEnded = false }) => {
  const [field, setField] = useState('quotenumber');
  const [text, setText] = useState('');
  const [searchEnded, setSearchEnded] = useState(initialSearchEnded);

  // On open, prefill from the active search (so the user can edit + re-run it);
  // a one-value-per-line textarea is easiest to tweak. Empty → fresh box.
  useEffect(() => {
    if (!open) return;
    setSearchEnded(initialSearchEnded);   // restore the persisted toggle
    const raw = (initialBlob || '').split('|||').filter(Boolean)[0];
    if (raw) {
      const [f = '', , values = ''] = raw.split('~~');
      setField(SEARCH_FIELDS.some((s) => s.field === f) ? f : 'quotenumber');
      setText(values.split(',').map((v) => v.trim()).filter(Boolean).join('\n'));
    } else {
      setField('quotenumber');
      setText('');
    }
  }, [open, initialBlob, initialSearchEnded]);

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  const search = () => {
    const blob = buildSearchBlob(field, text);
    if (blob) { onApply(blob, searchEnded); onClose(); }
  };

  const inputCls = 'border border-slate-300 dark:border-slate-600 rounded px-1.5 py-1 text-[0.75rem] bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/45 backdrop-blur-sm p-4 overflow-y-auto"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        role="dialog"
        aria-label="Custom Search"
        className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-xl border border-slate-200 dark:border-slate-700"
      >
        <ModalHeader title="Custom Search" onClose={onClose} dragHandleProps={dragHandleProps} />
        <div className="px-4 py-3 flex flex-col gap-2">
          <p className="text-[0.75rem] text-slate-500 dark:text-slate-400">
            Paste values — one per line or comma-separated — then choose what to search by.
          </p>
          <textarea
            aria-label="Search values"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={10}
            className={`${inputCls} w-full resize-y`}
            placeholder={'1050\n1051\n1052'}
          />
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[0.75rem] font-semibold text-slate-600 dark:text-slate-300">Search By:</span>
            <select aria-label="Search By" className={inputCls} value={field} onChange={(e) => setField(e.target.value)}>
              {SEARCH_FIELDS.map((f) => <option key={f.field} value={f.field}>{f.label}</option>)}
            </select>
            {/* Include ended/expired quotes in the search. Persisted to user settings. */}
            <label className="flex items-center gap-1.5 text-[0.75rem] font-medium text-slate-600 dark:text-slate-300 cursor-pointer select-none">
              <Toggle size="sm" checked={searchEnded} onChange={setSearchEnded} ariaLabel="Search ended quotes" />
              Search Ended
            </label>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-[0.78125rem] font-medium text-slate-700 bg-white border border-slate-300 rounded dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600">Cancel</button>
          <button type="button" onClick={search} disabled={!text.trim()} className="px-4 py-1.5 text-[0.78125rem] font-semibold text-white bg-blue-600 border border-blue-700 rounded hover:bg-blue-700 disabled:opacity-40">Search</button>
        </div>
      </div>
    </div>
  );
};

export default CustomSearchDialog;
